import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Plus,
  AlignJustify,
  Building2,
  UserCheck,
  CreditCard,
  Package,
  Settings,
  HelpCircle,
  LogOut,
  ChevronLeft,
  FileText,
  Gift,
} from "lucide-react";
import { logout } from "../../../services/authService.js";
import { useApp } from "../../../hooks/useApp.js";
import { goToNewFiling } from "../../../utils/newFiling.js";
import HelpSupportModal from "../../ui/HelpSupportModal/HelpSupportModal.jsx";

const NAV = [
  {
    section: "Filing",
    items: [
      { id: "dashboard", Icon: LayoutDashboard, label: "Dashboard", to: "/dashboard" },
      { id: "new-filing", Icon: Plus, label: "New Filing", action: "newFiling" },
      { id: "filings", Icon: AlignJustify, label: "My Filings", to: "/dashboard/filings" },
      { id: "businesses", Icon: Building2, label: "Businesses", to: "/dashboard/businesses" },
      { id: "preparers", Icon: UserCheck, label: "Tax Preparers", to: "/dashboard/preparers" },
    ],
  },
  {
    section: "Finance",
    items: [
      { id: "subscriptions", Icon: Package, label: "Subscriptions", to: "/dashboard/subscriptions" },
      { id: "payments", Icon: CreditCard, label: "Payment History", to: "/dashboard/payments" },
      { id: "referrals", Icon: Gift, label: "Referral Credit", to: "/dashboard/referrals" },
    ],
  },
  {
    section: "Account",
    items: [
      { id: "settings", Icon: Settings, label: "Settings", to: "/dashboard/settings" },
    ],
  },
];

export default function Sidebar({ collapsed, onToggle }) {
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;
  const { dispatch, showToast } = useApp();
  const [helpOpen, setHelpOpen] = useState(false);

  // Goes straight into the wizard's Form step (FormTypeStep.jsx), which has
  // its own Business/Tax Preparer/filing-type pickers - so "New Filing"
  // doesn't make the filer pick a filing type twice on two separate pages.
  // SEED resets any in-progress wizard state first, so this always starts a
  // clean filing.
  //
  // goToNewFiling (utils/newFiling.js) guards this the same way the Form
  // 720 reference's DashboardLayout.openNewFiling does: a brand-new user
  // with no saved businesses is sent to the Business Details form first,
  // then on into the Form step once that first business is saved.
  const startNewFiling = () => {
    goToNewFiling({ dispatch, navigate, showToast });
  };

  // Resets AppContext state (and the businessId/preparerId/returnId it
  // persists to sessionStorage - see AppContext.jsx's PERSISTED_ID_KEYS)
  // before leaving. Without this, sessionStorage keeps whatever the
  // previous account left behind, so signing in as a different user in the
  // same tab silently reuses their old businessId/returnId - the new
  // account's own trucks/business never loads because the app is still
  // pointed at someone else's record.
  const handleSignOut = async () => {
    await logout();
    dispatch({ type: "SEED", which: "new" });
    navigate("/login");
  };

  function isActive(item) {
    if (item.to) return path === item.to;
    // "New Filing" navigates via startNewFiling() (an onClick action, not a
    // <Link to>) straight into /wizard/1 - the same URL "Continue filing"
    // (My Filings/Dashboard) also lands on, so there's no page-specific path
    // left to key off; it just never highlights once inside the wizard,
    // same as every other sidebar item does while there.
    return false;
  }

  function renderItem(item) {
    const active = isActive(item);
    const { Icon } = item;

    const baseCls = [
      "flex items-center rounded-[8px] text-[13.5px] font-medium cursor-pointer relative",
      "transition-all duration-150 mb-[2px] select-none outline-none",
      collapsed ? "justify-center w-10 h-10 mx-auto p-0" : "gap-[10px] px-[10px] py-[9px]",
      active
        ? "bg-[var(--secondary)] text-white before:content-[''] before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:w-[3px] before:h-[60%] before:rounded-full before:bg-[var(--accent)]"
        : "text-[var(--line)] hover:bg-[var(--primary-hover)] hover:text-white",
    ].join(" ");

    const iconEl = (
      <Icon size={17} strokeWidth={active ? 2.2 : 1.8} className="flex-shrink-0" style={{ minWidth: 17 }} />
    );
    const labelEl = !collapsed && <span className="flex-1 whitespace-nowrap leading-none">{item.label}</span>;
    const content = (
      <>
        {iconEl}
        {labelEl}
      </>
    );

    if (item.action === "newFiling") {
      return (
        <div key={item.id} title={collapsed ? item.label : undefined} className={baseCls} onClick={startNewFiling}>
          {content}
        </div>
      );
    }

    return (
      <Link key={item.id} to={item.to} title={collapsed ? item.label : undefined} className={`${baseCls} no-underline`}>
        {content}
      </Link>
    );
  }

  return (
    <>
    <aside
      className={[
        collapsed ? "w-[64px]" : "w-[240px]",
        "bg-[var(--primary)] border-r border-black/10",
        "flex flex-col fixed top-0 left-0 bottom-0 z-[100]",
        "transition-[width] duration-[240ms] ease-[cubic-bezier(0.4,0,0.2,1)]",
        "overflow-hidden",
      ].join(" ")}
    >
      {/* ── Header ── */}
      <div
        className={[
          "flex-shrink-0 flex items-center h-[60px] border-b border-[rgba(255,255,255,0.07)] relative",
          collapsed ? "justify-center px-0" : "px-4 gap-[10px]",
        ].join(" ")}
      >
        <div className="w-8 h-8 rounded-[9px] bg-white/15 flex items-center justify-center flex-shrink-0">
          <FileText size={15} strokeWidth={2.2} className="text-white" />
        </div>

        {!collapsed && (
          <span className="text-[15px] font-extrabold text-white tracking-[-0.02em] whitespace-nowrap flex-1 font-disp">
            EasyFile<span className="text-[var(--accent)]">2290</span>
          </span>
        )}

        {!collapsed && (
          <button
            onClick={onToggle}
            title="Collapse sidebar"
            className="w-[26px] h-[26px] rounded-[7px] bg-[rgba(255,255,255,0.08)] border border-[rgba(255,255,255,0.1)] flex items-center justify-center cursor-pointer text-[rgba(255,255,255,0.55)] hover:bg-[rgba(255,255,255,0.15)] hover:text-white transition-all flex-shrink-0"
          >
            <ChevronLeft size={14} strokeWidth={2} />
          </button>
        )}

        {collapsed && (
          <button
            onClick={onToggle}
            title="Expand sidebar"
            className="absolute top-[17px] right-[19px] w-[26px] h-[26px] rounded-[7px] bg-[rgba(255,255,255,0.08)] border border-[rgba(255,255,255,0.1)] flex items-center justify-center cursor-pointer text-[rgba(255,255,255,0.55)] hover:bg-[rgba(255,255,255,0.15)] hover:text-white transition-all"
          >
            <ChevronLeft size={14} strokeWidth={2} className="rotate-180" />
          </button>
        )}
      </div>

      {/* ── Nav ── */}
      <nav className={`flex-1 overflow-y-auto py-3 ${collapsed ? "px-[10px]" : "px-3"}`}>
        {NAV.map(({ section, items }) => (
          <div key={section} className="mb-1">
            {!collapsed ? (
              <div className="text-[10.5px] font-bold text-[rgba(255,255,255,0.38)] uppercase tracking-[0.12em] px-[10px] pt-[14px] pb-[6px] whitespace-nowrap">
                {section}
              </div>
            ) : (
              <div className="pt-[14px] pb-[6px] flex justify-center">
                <div className="w-4 h-px bg-[rgba(255,255,255,0.12)]" />
              </div>
            )}
            {items.map(renderItem)}
          </div>
        ))}
      </nav>

      {/* ── Bottom: Help + Sign out ── */}
      <div
        className={[
          "flex-shrink-0 border-t border-[rgba(255,255,255,0.07)]",
          collapsed ? "px-[10px] py-3 flex flex-col items-center gap-1" : "px-3 py-3 flex flex-col gap-[2px]",
        ].join(" ")}
      >
        <div
          onClick={() => setHelpOpen(true)}
          title={collapsed ? "Help & Support" : undefined}
          className={[
            "flex items-center rounded-[8px] text-[13.5px] font-medium cursor-pointer",
            "transition-all duration-150 text-[var(--line)] hover:bg-[rgba(255,255,255,0.09)] hover:text-white",
            collapsed ? "justify-center w-10 h-10" : "gap-[10px] px-[10px] py-[9px]",
          ].join(" ")}
        >
          <HelpCircle size={17} strokeWidth={1.8} className="flex-shrink-0" style={{ minWidth: 17 }} />
          {!collapsed && <span className="flex-1">Help &amp; Support</span>}
        </div>

        <div
          onClick={handleSignOut}
          title={collapsed ? "Sign out" : undefined}
          className={[
            "flex items-center rounded-[8px] text-[13.5px] font-medium cursor-pointer",
            "transition-all duration-150 text-[rgba(239,68,68,0.55)] hover:bg-[rgba(239,68,68,0.09)] hover:text-[#f87171]",
            collapsed ? "justify-center w-10 h-10" : "gap-[10px] px-[10px] py-[9px]",
          ].join(" ")}
        >
          <LogOut size={17} strokeWidth={1.8} className="flex-shrink-0" style={{ minWidth: 17 }} />
          {!collapsed && <span className="flex-1">Sign out</span>}
        </div>
      </div>
    </aside>

    <HelpSupportModal open={helpOpen} onClose={() => setHelpOpen(false)} />
    </>
  );
}
